import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import type {
  MapStateToProps,
  MapDispatchToProps,
  ThunkAction,
} from 'types/redux';

import {
  Composites,
  FontFamilyPicker,
  InfoIcon,
  TextLabel,
  Tooltip,
} from '@wix/wix-base-ui';
import experiment from 'experiment';

import { translate } from '#packages/i18n';
import * as util from '#packages/util';
import { symbol as Symbol } from '@wix/santa-editor-symbols';
import type { FontOption } from 'types/documentServices';
import type { BiEventFields } from 'types/bi';
import type { EditorAPI } from '@/editorAPI';
import { bi } from '@/stateManagement';
import * as coreBi from '@/coreBi';

import {
  isUploadedFont,
  getFont,
  getOnlyFontFamily,
} from '../../utils/fontUtils';
import constants from '../../utils/constants';

import {
  FontPicker,
  type FontOption as PickerFontOption,
  type OnPreviewBIData,
  type FontPickerBiData,
} from '../fontPicker';
import { fontWeight, isFontWeight } from '../../actions';
import { fontUtils } from '../../index';

const { FONT_GROUPS } = constants;

interface FooterLink {
  label: string;
  onClick: () => void;
  className?: string;
  symbol?: string;
}

interface FontFamilyStateProps {}

interface FontFamilyDispatchProps {
  onOpenLanguageSupportClick: () => void;
  onOpenFontsUploadClick: () => void;
}

export type FontFamilyBiData = FontPickerBiData;

interface FontFamilyOwnProps {
  shouldTranslate?: boolean;
  shouldAddAsteriskToSelectedFont?: boolean;
  infoText?: string;
  label?: string;
  value?: string;
  weight?: number | string;
  doNotFocus?: boolean;
  footerLinks?: FooterLink[];
  fonts: FontOption[];
  valueLink?: {
    value: string;
    requestChange: (value: string, weight?: number | string) => void;
  };
  automationId?: string;
  tooltipValue?: string | React.ReactElement;
  toolbarMode?: boolean;
  showWeightPicker?: boolean;
  showWeightOptions?: boolean;
  showOldFontPicker?: boolean;
  biData?: FontFamilyBiData;
  onChange?: (fontFamily: string, fontWeight?: string) => void;
  onPreview?: (
    fontFamily: string,
    fontWeight: string | undefined,
    biData: OnPreviewBIData,
  ) => void;
  onClose?: () => void;
  onToggle?: (isOpen: boolean) => void;
}

type FontFamilyProps = FontFamilyOwnProps &
  FontFamilyDispatchProps &
  FontFamilyStateProps;

class FontFamily extends React.Component<FontFamilyProps> {
  private footerLinks: FooterLink[] | undefined;

  constructor(props: FontFamilyProps) {
    super(props);
    const { onOpenLanguageSupportClick, onOpenFontsUploadClick } = props;
    const shouldRenderNewPicker = experiment.isOpen('se_newFontPicker');

    if (shouldRenderNewPicker) {
      this.footerLinks = [
        {
          label: 'text_editor_font_dropdown_other_languages_link',
          onClick: () => {
            onOpenLanguageSupportClick();
          },
          symbol: 'textAddLang',
          className: 'add-lang-link',
        },
      ];

      const isTemplateUser = experiment.isOpen('se_templateDesignerUsers');
      if (!isTemplateUser) {
        this.footerLinks.push({
          label: 'text_editor_fonts_dropdown_upload_fonts',
          onClick: () => {
            onOpenFontsUploadClick();
          },
          symbol: 'textUploadFonts',
          className: 'upload-font-link',
        });
      }
    }
  }

  getUploadedFontName = (fontFamily: string = '') => {
    let originalFontName = fontFamily.split(',')[2] || fontFamily;

    originalFontName = originalFontName.replace(
      util.fontsManagerUtils.constants.ORIGINAL_FONT_PREFIX,
      '',
    );
    originalFontName = _.startCase(originalFontName);
    return originalFontName;
  };

  getMissingFontName = () => {
    if (isUploadedFont(this.props.value)) {
      return this.getUploadedFontName(this.props.value);
    }
    if (this.props.value && getFont(this.props.value)) {
      return getFont(this.props.value).displayName;
    }
    return '';
  };

  translateIfNeeded = (text: string) => {
    if (
      _.isBoolean(this.props.shouldTranslate) &&
      !this.props.shouldTranslate
    ) {
      return text;
    }

    return translate(text);
  };

  getFooterLinks = () => {
    const links = this.props.footerLinks || this.footerLinks;

    return links?.map((footerLink) => ({
      ...footerLink,
      label: this.translateIfNeeded(footerLink.label),
      symbol: footerLink.symbol && <Symbol name={footerLink.symbol} />,
    }));
  };

  handleChange = (family: string, weight?: number) => {
    const fontWeight =
      weight !== undefined ? this.getFontWeightFromNumber(weight) : undefined;

    return this.props.onChange
      ? this.props.onChange(family, fontWeight)
      : this.props.valueLink.requestChange(family, fontWeight);
  };

  handleArrowKeyDown = (e: AnyFixMe) => {
    this.props.onPreview?.(e.props.value, undefined, {
      origin: 'keyboard',
    });
  };

  handleOptionMouseEnter = (e: AnyFixMe) => {
    this.props.onPreview?.(e.props.value, undefined, {
      origin: 'mouse',
    });
  };

  handlePreview = (
    family: string,
    weight: number | undefined,
    biData: OnPreviewBIData,
  ) => {
    if (this.props.onPreview) {
      const fontWeight =
        weight !== undefined ? this.getFontWeightFromNumber(weight) : undefined;
      this.props.onPreview(family, fontWeight, biData);
    }
  };

  getFontGroupsNamesMap = () => {
    return Object.keys(FONT_GROUPS).reduce(
      (map, key) => {
        map[key as keyof typeof FONT_GROUPS] = translate(
          FONT_GROUPS[key as keyof typeof FONT_GROUPS],
        );
        return map;
      },
      {} as Record<keyof typeof FONT_GROUPS, string>,
    );
  };

  getFontWeightFromNumber = (value: number): string => {
    if (value === 700) {
      return fontWeight.Bold;
    }

    if (value === 400) {
      return fontWeight.Normal;
    }

    const fontWeightAsString = `${value}`;

    if (isFontWeight(fontWeightAsString)) {
      return fontWeightAsString;
    }

    return fontWeight.Normal;
  };

  getFontWeightAsNumber = (value: number | string | undefined): number => {
    if (value === undefined) {
      return 400;
    }

    return fontUtils.getFontWeightAsNumber(value);
  };

  addAsteriskToSelectedFont = (fontOptions: FontOption[]) => {
    // TODO: add this to base UI;
    const fontOptionsToReturn = _.cloneDeep(fontOptions);

    for (const fontOption of fontOptionsToReturn) {
      for (const font of fontOption.fonts) {
        if (font.fontFamily === this.props.value) {
          font.displayName = `${font.displayName}*`;

          return fontOptionsToReturn;
        }
      }
    }

    return fontOptionsToReturn;
  };

  getFontPostfix = (font: PickerFontOption) => {
    if (this.props.value === font?.family) {
      return '*';
    }

    return;
  };

  render() {
    const {
      fonts,
      weight,
      showWeightPicker,
      showOldFontPicker = false,
      biData,
    } = this.props;
    const fontGroupsNamesMap = this.getFontGroupsNamesMap();
    const fontValue = getOnlyFontFamily(
      this.props.value || this.props.valueLink?.value,
    );

    const shouldRenderNewPicker =
      !showOldFontPicker && experiment.isOpen('se_newFontPicker');

    if (shouldRenderNewPicker) {
      const footerActions = this.getFooterLinks();
      const getFontSuffix = this.props.shouldAddAsteriskToSelectedFont
        ? this.getFontPostfix
        : undefined;

      return (
        <Tooltip
          content={this.props.tooltipValue}
          disabled={!this.props.tooltipValue}
        >
          <Composites.FontFamilyPickerLabeled>
            {this.props.infoText && (
              <InfoIcon text={this.props.infoText} shouldTranslate={true} />
            )}
            {this.props.label && (
              <TextLabel value={this.props.label} shouldTranslate={true} />
            )}
            <FontPicker
              family={fontValue}
              weight={this.getFontWeightAsNumber(weight)}
              showWeightPicker={showWeightPicker}
              showWeightOptions={showWeightPicker}
              biData={biData}
              getFontSuffix={getFontSuffix}
              onChange={this.handleChange}
              onPreview={this.handlePreview}
              onToggle={this.props.onToggle}
              onAddLanguagesClick={footerActions[0]?.onClick}
              onUploadFontsClick={footerActions[1]?.onClick}
            />
          </Composites.FontFamilyPickerLabeled>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        content={this.props.tooltipValue}
        disabled={!this.props.tooltipValue}
      >
        <Composites.FontFamilyPickerLabeled>
          {this.props.infoText ? (
            <InfoIcon text={this.props.infoText} shouldTranslate={true} />
          ) : null}
          {this.props.label ? (
            <TextLabel value={this.props.label} shouldTranslate={true} />
          ) : null}
          <FontFamilyPicker
            key={this.props.value}
            fonts={
              this.props.shouldAddAsteriskToSelectedFont
                ? this.addAsteriskToSelectedFont(fonts)
                : fonts
            }
            fontGroupsNamesMap={fontGroupsNamesMap}
            value={fontValue}
            shouldTranslate={false}
            onToggle={this.props.onToggle}
            onChange={this.handleChange}
            onArrowKeyDown={this.handleArrowKeyDown}
            onOptionMouseEnter={this.handleOptionMouseEnter}
            // @ts-expect-error
            optionsContainerClassName="fonts-dropdown"
            getMissingFontName={this.getMissingFontName}
            toolbarMode={this.props.toolbarMode}
            footerLinks={this.getFooterLinks()}
            automationId={this.props.automationId}
            shouldUseSelectedFontFamily={true}
          />
        </Composites.FontFamilyPickerLabeled>
      </Tooltip>
    );
  }

  static propTypes = {
    shouldTranslate: PropTypes.bool,
    infoText: PropTypes.string,
    label: PropTypes.string,
    onClose: PropTypes.func,
    onToggle: PropTypes.func,
    onChange: PropTypes.func,
    doNotFocus: PropTypes.bool,
    footerLinks: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
      }),
    ),
    fonts: PropTypes.arrayOf(
      PropTypes.shape({
        fonts: PropTypes.array,
        lang: PropTypes.string.isRequired,
      }).isRequired,
    ),
    value: PropTypes.string,
    valueLink: PropTypes.shape({
      value: PropTypes.object,
      requestChange: PropTypes.func.isRequired,
    }),
    automationId: PropTypes.string,
    // type inferring from proptypes breaks type resolving with i18n. We have props interfaces, so we can add any here
  } as any;

  static defaultProps = {
    onToggle: _.noop,
  };
}

const getEditorAPI: ThunkAction = (_dispatch, _getState, { editorAPI }) =>
  editorAPI;

const mapStateToProps: MapStateToProps<
  FontFamilyStateProps,
  FontFamilyOwnProps
> = () => {
  return {};
};

const mapDispatchToProps: MapDispatchToProps<
  FontFamilyDispatchProps,
  FontFamilyOwnProps
> = (dispatch, ownProps) => {
  const editorAPI: EditorAPI = dispatch(getEditorAPI);

  const sendBI = (evid: number, props: BiEventFields = {}) => {
    dispatch(
      bi.actions.event(
        { evid },
        {
          ...props,
          panel_name: ownProps.biData?.panelName ?? 'unknown_panel',
        },
      ),
    );
  };

  return {
    onOpenLanguageSupportClick: () => {
      sendBI(
        coreBi.events.textControls.Text_Panel_Font_Language_Link_Click.evid,
      );

      editorAPI.panelManager.openPanel(
        'panels.toolPanels.languageSupport',
        {},
        true,
      );
    },
    onOpenFontsUploadClick: () => {
      sendBI(coreBi.events.textControls.Text_Panel_Font_Upload_Link_Click.evid);

      editorAPI.panelManager.openPanel(
        'panels.toolPanels.fontsUploadPanel',
        {},
        true,
      );
    },
  };
};

const FontFamilyConnected = util.hoc.connect(
  util.hoc.STORES.EDITOR_API,
  mapStateToProps,
  mapDispatchToProps,
)(FontFamily);

export default FontFamilyConnected;

export { type OnPreviewBIData };
